import { DocumentsType, Inspection, InspectionCheckListItems } from "../types";

// Define interfaces for the original API response
interface TimeSlot {
    StartTime: string;
    EndTime: string;
  }
  
  interface InspectorAssignment {
    InspectionDate: string;
    NoteForInspector: string;
    TimeSlot: TimeSlot;
  }
  
  interface ApplicationDetails {
    ApplicantName: string;
    ApplicationTitle: string;
    OwnerMobileNumber: string;
    ProjectLocation: string;
  }
  
  interface InspectorDetails {
    FirstName: string;
    LastName: string;
    EmailID: string;
    PhoneNumber: string;
  }
  
  interface AvailabilityOption {
    Date: string;
    TimeSlots: string[];
  }
  
  interface OriginalInspection {
    ID: number;
    ApplicationNumber: string;
    ApplicationID: number;
    SubcodeNameID: number;
    SubcodeName: string;
    InspectionTypeID: number;
    InspectionType: string;
    InspectionNote: string;
    AvailabilityOptions: AvailabilityOption[];
    Documents: DocumentsType[];
    Status: string;
    InspectorID: number;
    CreatedDate: string;
    CreatedBy: number;
    TownshipID: number;
    UpdatedDate: string;
    UpdatedBy: number;
    ApplicationDetails: ApplicationDetails;
    InspectorDetails: InspectorDetails;
    InspectorAssignments: InspectorAssignment[];
    InspectionCheckList: InspectionCheckListItems[]
  }
  
  // Define interface for the transformed data
//   interface TransformedInspection {
//     id: string;
//     applicationId: string;
//     applicationNumber: string;
//     applicationTitle: string;
//     applicantName: string;
//     propertyAddress: string;
//     inspectionDate: string | null;
//     timeSlot: string | null;
//     status: string;
//     priority: string;
//     notes: string;
//     subcode: string;
//     inspectionType: string;
//     contactName: string;
//     contactPhone: string;
//     completedAt: null;
//     completionNotes: null;
//     attachments: any[];
//   }
  
  /**
   * Transforms inspection data from the original API format to the desired format
   * 
   * @param originalData - Array of inspection data from the API
   * @returns Array of transformed inspection data
   */
  export default function transformInspectionData(originalData: OriginalInspection[]): Inspection[] {
    return originalData.map(item => {
      // Extract the assigned inspection date and time slot
      let inspectionDate: string | null = null;
      let timeSlot: string | null = null;
      
      if (item.InspectorAssignments && item.InspectorAssignments.length > 0) {
        const assignment = item.InspectorAssignments[0];
        
        if (assignment.InspectionDate) {
          // Format date to YYYY-MM-DD
          inspectionDate = new Date(assignment.InspectionDate).toISOString().split('T')[0];
        }
        
        if (assignment.TimeSlot) {
          // Convert time slots from StartTime and EndTime to formatted string
          const startTime = assignment.TimeSlot.StartTime;
          const endTime = assignment.TimeSlot.EndTime;
          
          if (startTime && endTime) {
            // Convert 24-hour format to AM/PM format
            const formatTime = (timeStr: string): string => {
              const [hours, minutes] = timeStr.split(':');
              const hour = parseInt(hours);
              const ampm = hour >= 12 ? 'PM' : 'AM';
              const hour12 = hour % 12 || 12;
              return `${hour12}:${minutes} ${ampm}`;
            };
            
            const formattedStartTime = formatTime(startTime);
            const formattedEndTime = formatTime(endTime);
            timeSlot = `${formattedStartTime} - ${formattedEndTime}`;
          }
        }
      }
      
      // Construct the transformed object
      return {
        id: `INSP-2025-${item.ID * 1000}`, // Creating unique ID pattern
        inspectionId: String(item?.ID),
        applicationId: `APP-2025-${item.ApplicationID * 10}`, // Creating unique application ID pattern
        applicationNumber: item.ApplicationNumber,
        applicationTitle: item.ApplicationDetails?.ApplicationTitle || "",
        applicantName: item.ApplicationDetails?.ApplicantName || "",
        propertyAddress: item.ApplicationDetails?.ProjectLocation || "",
        documents: item?.Documents,
        inspectionDate,
        inspectionNote: item?.InspectionNote,
        timeSlot,
        status: item.Status,
        priority: "Medium", // Default value as it's not in the original data
        notes: item.InspectorAssignments?.[0]?.NoteForInspector || "",
        subcode: item.SubcodeName,
        inspectionType: item.InspectionType,
        contactName: `${item.InspectorDetails?.FirstName || ""} ${item.InspectorDetails?.LastName || ""}`.trim(),
        contactPhone: item.InspectorDetails?.PhoneNumber || "",
        completedAt: null, // Not available in the original data
        completionNotes: null, // Not available in the original data
        attachments: [], // Not available in the original data
        inspectionCheckList: item?.InspectionCheckList
      };
    });
  }
  
  // Usage example:
  // const transformedData = transformInspectionData(apiResponse);